import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, ThemeProvider, Container, Typography, useMediaQuery, Box, TextField } from "@mui/material";
import { Form, Input, Button, message, Select, Upload, Row, Col } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import Sidebars from "../../Components/Sidebar/sidebar";
import '../../css/vehicles.css';
import appColors from "../../config/appColors";
import { postRequest, postRequestFormData } from "../../Api";
import { apiRoutes } from "../../Api/apiRoutes";
import { setLoader } from "../../Redux/Reducers/gernalSlice";
import { UploadOutlined, DeleteOutlined } from "@ant-design/icons";

const { Option } = Select;

const EditUser = () => {
    const { id } = useParams();
    const theme = useTheme();
    const dispatch = useDispatch();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector((state) => state.authReducer.user);
    const [form] = Form.useForm();

    // Existing form states
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [alternatePhone, setAlternatePhone] = useState('');
    const [licenseNumber, setLicenseNumber] = useState('');
    const [licenseExpDate, setLicenseExpDate] = useState('');
    const [postalCode, setPostalCode] = useState('');
    const [address, setAddress] = useState('');
    const [data, setData] = useState(null);
    
    // New form states for missing fields
    const [dob, setDob] = useState('');
    const [status, setStatus] = useState('1');
    const [kinsName, setKinsName] = useState('');
    const [kinsAddress, setKinsAddress] = useState('');
    const [kinsPhone, setKinsPhone] = useState('');
    const [kinsEmail, setKinsEmail] = useState('');
    const [kinsRelation, setKinsRelation] = useState('');
    
    // Document states
    const [pcoLicenseFront, setPcoLicenseFront] = useState(null);
    const [pcoLicenseBack, setPcoLicenseBack] = useState(null);
    const [drivingLicenseFront, setDrivingLicenseFront] = useState(null);
    const [drivingLicenseBack, setDrivingLicenseBack] = useState(null);
    const [proofOfAddress, setProofOfAddress] = useState(null);
    const [pcoLicensePaper, setPcoLicensePaper] = useState(null);
    
    const [existingDocuments, setExistingDocuments] = useState([]);
    const [newDocumentList, setNewDocumentList] = useState([]);
    const [deletedDocIds, setDeletedDocIds] = useState([]);

    const history = useNavigate();

    const getCustomerDetails = async () => {
        const onSuccess = (res) => {
            dispatch(setLoader(false));
            if (res.data) {
                const data = res.data;
                setData(data);
                
                // Existing fields
                setName(data.name);
                setEmail(data.email);
                setPhone(data.phone);
                setAlternatePhone(data.alternate_phone);
                setPostalCode(data.postal_code);
                setAddress(data.address);
                setLicenseNumber(data.license_number);
                
                // New fields
                if (data.dob) {
                    let dobDate = new Date(data.dob);
                    let dobDay = dobDate.getDate().toString().padStart(2, '0');
                    let dobMonth = (dobDate.getMonth() + 1).toString().padStart(2, '0');
                    let dobYear = dobDate.getFullYear();
                    setDob(`${dobYear}-${dobMonth}-${dobDay}`);
                }
                
                setStatus(data.status?.toString());
                setKinsName(data.kins_name || '');
                setKinsAddress(data.kins_address || '');
                setKinsPhone(data.kins_phone || '');
                setKinsEmail(data.kins_email || '');
                setKinsRelation(data.kins_relation || '');
                
                // License expiry date
                if (data.license_exp_date) {
                    let date = new Date(data.license_exp_date);
                    let day = date.getDate().toString().padStart(2, '0');
                    let month = (date.getMonth() + 1).toString().padStart(2, '0');
                    let year = date.getFullYear();
                    setLicenseExpDate(`${year}-${month}-${day}`);
                }
                
                // Set existing document paths
                setExistingDocuments([
                    { field: 'pco_license_front', value: data.pco_license_front },
                    { field: 'pco_license_back', value: data.pco_license_back },
                    { field: 'driving_license_front', value: data.driving_license_front },
                    { field: 'driving_license_back', value: data.driving_license_back },
                    { field: 'proof_of_address', value: data.proof_of_address },
                    { field: 'pco_license_paper', value: data.pco_license_paper },
                ].filter(doc => doc.value));
            }
        }
        const onError = (err) => {
            console.log('err', err)
            err?.data?.error && alert(err.data.error);
            dispatch(setLoader(false));
        }

        const body = {
            cust_id: id,
            token: user?.token
        }
        dispatch(setLoader(true));
        await postRequest(body, apiRoutes.getCustDetails, onSuccess, onError);
    }

    const handleFileChange = (file, field) => {
        switch(field) {
            case 'pco_license_front': setPcoLicenseFront(file); break;
            case 'pco_license_back': setPcoLicenseBack(file); break;
            case 'driving_license_front': setDrivingLicenseFront(file); break;
            case 'driving_license_back': setDrivingLicenseBack(file); break;
            case 'proof_of_address': setProofOfAddress(file); break;
            case 'pco_license_paper': setPcoLicensePaper(file); break;
            default: break;
        }
        return false; // Prevent automatic upload
    };

    const removeDocument = (field) => {
        switch(field) {
            case 'pco_license_front': setPcoLicenseFront(null); break;
            case 'pco_license_back': setPcoLicenseBack(null); break;
            case 'driving_license_front': setDrivingLicenseFront(null); break;
            case 'driving_license_back': setDrivingLicenseBack(null); break;
            case 'proof_of_address': setProofOfAddress(null); break;
            case 'pco_license_paper': setPcoLicensePaper(null); break;
            default: break;
        }
    };

    const doEditCustomer = async () => {
        // Validation
        if (!email) return message.error('Please enter a valid email');
        if (!name) return message.error('Please enter name');
        if (!phone) return message.error('Please enter phone');
        if (!alternatePhone) return message.error('Please enter alternate number');
        if (!licenseNumber) return message.error('Please enter license number');
        if (!licenseExpDate) return message.error('Please enter license expiry date');
        if (!postalCode) return message.error('Please enter postal code');
        if (!address) return message.error('Please enter address');
        if (!dob) return message.error('Please enter date of birth');
        if (!kinsName) return message.error('Please enter next of kin name');

        const onSuccess = (res) => {
            console.log('res', res)
            dispatch(setLoader(false))
            message.success("Customer updated successfully!");
            history("/users");
        }
        const onError = (err) => {
            console.log('err', err)
            dispatch(setLoader(false))
            alert(err?.data?.error);
        }

        const formData = new FormData();
        formData.append('token', user?.token);
        formData.append('cust_id', id);
        formData.append('name', name);
        formData.append('email', email);
        formData.append('phone', phone);
        formData.append('alternate_phone', alternatePhone);
        formData.append('license_number', licenseNumber);
        formData.append('license_exp_date', licenseExpDate);
        formData.append('postal_code', postalCode);
        formData.append('address', address);
        formData.append('dob', dob);
        formData.append('status', status);
        formData.append('kins_name', kinsName);
        formData.append('kins_address', kinsAddress);
        formData.append('kins_phone', kinsPhone);
        formData.append('kins_email', kinsEmail);
        formData.append('kins_relation', kinsRelation);

        // Append files if they exist
        if (pcoLicenseFront) formData.append('pco_license_front', pcoLicenseFront);
        if (pcoLicenseBack) formData.append('pco_license_back', pcoLicenseBack);
        if (drivingLicenseFront) formData.append('driving_license_front', drivingLicenseFront);
        if (drivingLicenseBack) formData.append('driving_license_back', drivingLicenseBack);
        if (proofOfAddress) formData.append('proof_of_address', proofOfAddress);
        if (pcoLicensePaper) formData.append('pco_license_paper', pcoLicensePaper);

        // Only send email/phone if changed
        if (data && email === data.email) formData.delete('email');
        if (data && phone === data.phone) formData.delete('phone');

        dispatch(setLoader(true));
        await postRequestFormData(formData, apiRoutes.editCustomer, onSuccess, onError);
    }

    useEffect(() => {
        getCustomerDetails();
    }, [])

    const documentFields = [
        { label: 'PCO License Front', field: 'pco_license_front', state: pcoLicenseFront, setter: setPcoLicenseFront },
        { label: 'PCO License Back', field: 'pco_license_back', state: pcoLicenseBack, setter: setPcoLicenseBack },
        { label: 'Driving License Front', field: 'driving_license_front', state: drivingLicenseFront, setter: setDrivingLicenseFront },
        { label: 'Driving License Back', field: 'driving_license_back', state: drivingLicenseBack, setter: setDrivingLicenseBack },
        { label: 'Proof of Address', field: 'proof_of_address', state: proofOfAddress, setter: setProofOfAddress },
        { label: 'PCO License Paper', field: 'pco_license_paper', state: pcoLicensePaper, setter: setPcoLicensePaper },
    ];

    return (
        <>
            <ThemeProvider theme={theme}>
                <div style={{ display: 'flex', }}>
                    <Sidebars />
                    <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 20, width: "75%" }} >
                        <span className={`headertext ${isSmallScreen ? 'small-screen' : ''}`}>
                            Edit Customer
                        </span>
                        <div style={{ display: 'flex', flexDirection: 'column', width: "100%", borderRadius: 10, backgroundColor: appColors.white, marginTop: 10, paddingBottom: 20 }}>
                            <div style={{ width: "100%", height: 45, borderTopRightRadius: 10, borderTopLeftRadius: 10, backgroundColor: appColors.primary }}>
                                <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600, marginLeft: 20, marginTop: 10 }}>Customer Information</Typography>
                            </div>

                            <Form
                                style={{ width: "100%", paddingRight: 20, paddingLeft: 20 }}
                                layout="vertical"
                                form={form}
                                onFinish={doEditCustomer}
                                onFinishFailed={() => { }}
                            >
                                {/* Personal Information */}
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: "100%", justifyContent: 'space-between', marginTop: 20 }} >
                                    <Form.Item name="name">
                                        <Typography style={labelStyle}>Name *</Typography>
                                        <Input
                                            className="textInput"
                                            value={name}
                                            onChange={(e) => setName(e.target.value)}
                                        />
                                    </Form.Item>

                                    <Form.Item name="email">
                                        <Typography style={labelStyle}>Email *</Typography>
                                        <Input
                                            className="textInput"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="phone">
                                        <Typography style={labelStyle}>Phone *</Typography>
                                        <Input
                                            className="textInput"
                                            value={phone} 
                                            onChange={(e) => setPhone(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="alternatePhone">
                                        <Typography style={labelStyle}>Alternate Phone *</Typography>
                                        <Input
                                            className="textInput"
                                            value={alternatePhone} 
                                            onChange={(e) => setAlternatePhone(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="dob">
                                        <Typography style={labelStyle}>Date of Birth *</Typography>
                                        <Input
                                            className="textInput"
                                            type="date"
                                            value={dob}
                                            onChange={(e) => setDob(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="status">
                                        <Typography style={labelStyle}>Status</Typography>
                                        <Select
                                            className="textInput"
                                            value={status}
                                            onChange={setStatus}
                                            style={{ width: '100%' }}
                                        >
                                            <Option value="1">Active</Option>
                                            <Option value="0">Inactive</Option>
                                        </Select>
                                    </Form.Item>
                                </div>

                                {/* Address Information */}
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', }} >
                                    <Form.Item name="licenseNumber">
                                        <Typography style={labelStyle}>License Number *</Typography>
                                        <Input
                                            className="textInput"
                                            value={licenseNumber} 
                                            onChange={(e) => setLicenseNumber(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="licenseExpiryDate">
                                        <Typography style={labelStyle}>License Expiry Date *</Typography>
                                        <Input
                                            className="textInput"
                                            type="date"
                                            value={licenseExpDate}
                                            onChange={(e) => setLicenseExpDate(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="postalCode">
                                        <Typography style={labelStyle}>Postal Code *</Typography>
                                        <Input
                                            className="textInput"
                                            value={postalCode} 
                                            onChange={(e) => setPostalCode(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="address" style={{ flex: 2, minWidth: '300px' }}>
                                        <Typography style={labelStyle}>Address *</Typography>
                                        <Input.TextArea
                                            className="textInput"
                                            rows={3}
                                            value={address} 
                                            onChange={(e) => setAddress(e.target.value)} />
                                    </Form.Item>
                                </div>

                                {/* Next of Kin Information */}
                                <Typography style={{ fontSize: 18, fontWeight: 600, color: appColors.primary, marginTop: 30, marginBottom: 15 }}>
                                    Next of Kin Information
                                </Typography>
                                
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', }} >
                                    <Form.Item name="kinsName">
                                        <Typography style={labelStyle}>Name *</Typography>
                                        <Input
                                            className="textInput"
                                            value={kinsName}
                                            onChange={(e) => setKinsName(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="kinsRelation">
                                        <Typography style={labelStyle}>Relationship</Typography>
                                        <Input
                                            className="textInput"
                                            value={kinsRelation}
                                            onChange={(e) => setKinsRelation(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="kinsPhone">
                                        <Typography style={labelStyle}>Phone</Typography>
                                        <Input
                                            className="textInput"
                                            value={kinsPhone}
                                            onChange={(e) => setKinsPhone(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="kinsEmail">
                                        <Typography style={labelStyle}>Email</Typography>
                                        <Input
                                            className="textInput"
                                            value={kinsEmail}
                                            onChange={(e) => setKinsEmail(e.target.value)} />
                                    </Form.Item>

                                    <Form.Item name="kinsAddress" style={{ flex: 2, minWidth: '300px' }}>
                                        <Typography style={labelStyle}>Address</Typography>
                                        <Input.TextArea
                                            className="textInput"
                                            rows={3}
                                            value={kinsAddress}
                                            onChange={(e) => setKinsAddress(e.target.value)} />
                                    </Form.Item>
                                </div>

                                {/* Documents Section */}
                                <Typography style={{ fontSize: 18, fontWeight: 600, color: appColors.primary, marginTop: 30, marginBottom: 15 }}>
                                    Customer Documents
                                </Typography>

                                {/* Existing Documents */}
                                {existingDocuments.length > 0 && (
                                    <div style={{ marginBottom: 20 }}>
                                        <Typography style={{ fontSize: 14, marginBottom: 10, color: '#555' }}>Current Documents</Typography>
                                        {existingDocuments.map((doc) => (
                                            <div
                                                key={doc.field}
                                                style={{
                                                    padding: 12,
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: 8,
                                                    marginBottom: 8,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    border: '1px solid #e0e0e0'
                                                }}
                                            >
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <span style={{ fontSize: 24 }}>
                                                        {doc.value.endsWith('.pdf') ? '📄' : '🖼️'}
                                                    </span>
                                                    <div>
                                                        <div style={{ fontWeight: 600 }}>
                                                            {doc.field.replace(/_/g, ' ').toUpperCase()}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: '#888' }}>
                                                            {doc.value.split('/').pop()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Upload New Documents */}
                                <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 20 }}>
                                    {documentFields.map(({ label, field, state, setter }) => (
                                        <div key={field} style={{ minWidth: '200px' }}>
                                            <Typography style={labelStyle}>{label}</Typography>
                                            {state ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                                                    <span>{state.name}</span>
                                                    <Button
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={() => setter(null)}
                                                    />
                                                </div>
                                            ) : (
                                                <Upload
                                                    beforeUpload={(file) => {
                                                        setter(file);
                                                        return false;
                                                    }}
                                                    showUploadList={false}
                                                >
                                                    <Button icon={<UploadOutlined />} size="small">
                                                        Upload
                                                    </Button>
                                                </Upload>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Submit Button */}
                                <Form.Item style={{ textAlign: 'center', marginTop: 30 }}>
                                    <Button
                                        style={{
                                            height: 40, 
                                            backgroundColor: appColors.primary, 
                                            marginTop: 20,
                                            fontFamily: 'Poppins', 
                                            fontSize: 14, 
                                            fontWeight: 600, 
                                            width: 200
                                        }}
                                        type="primary"
                                        htmlType="submit">
                                        Save
                                    </Button>
                                </Form.Item>
                            </Form>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        </>
    )
}

const labelStyle = { 
    color: appColors.black, 
    fontSize: 14, 
    fontFamily: 'Poppins', 
    marginTop: 0,
    fontWeight: 500 
}

export default EditUser;

// import React, { useEffect, useState } from "react";

// import { useDispatch, useSelector } from 'react-redux';
// import { useTheme, ThemeProvider, Container, Typography, useMediaQuery, Box, TextField } from "@mui/material";
// import { Form, Input, Button, message, Select } from "antd";
// import { useNavigate, useParams } from "react-router-dom";
// import Sidebars from "../../Components/Sidebar/sidebar";
// import '../../css/vehicles.css';
// import appColors from "../../config/appColors";
// import { postRequest, postRequestFormData } from "../../Api";
// import { apiRoutes } from "../../Api/apiRoutes";
// import { setLoader } from "../../Redux/Reducers/gernalSlice";
// const { Option } = Select;


// const EditUser = () => {

//     const { id } = useParams();
//     const theme = useTheme();
//     const dispatch = useDispatch();

//     const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
//     const user = useSelector((state) => state.authReducer.user);
//     const [form] = Form.useForm();

//     const [name, setName] = useState('');
//     const [email, setEmail] = useState('');
//     const [phone, setPhone] = useState('');
//     const [alternatePhone, setAlternatePhone] = useState('');
//     const [licenseNumber, setLicenseNumber] = useState('');
//     const [licenseExpDate, setLicenseExpDate] = useState('')
//     const [postalCode, setPostalCode] = useState('');
//     const [address, setAddress] = useState('');
//     const [data, setData] = useState();

//     const history = useNavigate();


//     const getCustomerDetails = async () => {
//         const onSuccess = (res) => {
//             dispatch(setLoader(false));
//             if (res.data) {
//                 const data = res.data;
//                 setData(data);
//                 setName(data.name);
//                 setEmail(data.email);
//                 setPhone(data.phone);
//                 setAlternatePhone(data.alternate_phone);
//                 setPostalCode(data.postal_code);
//                 setAddress(data.address);
//                 setLicenseNumber(data.license_number);

//                 let date = new Date(data.license_exp_date);
//                 let day = date.getDate().toString().padStart(2, '0');
//                 let month = (date.getMonth() + 1).toString().padStart(2, '0');
//                 let year = date.getFullYear();
//                 let formattedDate = `${year}-${month}-${day}`;
//                 setLicenseExpDate(formattedDate);
//             }

//         }
//         const onError = (err) => {
//             console.log('err', err)
//             err?.data?.error && alert(err.data.error);
//             dispatch(setLoader(false));
//         }

//         const body = {
//             cust_id: id,
//             token: user?.token
//         }
//         dispatch(setLoader(true));
//         await postRequest(body, apiRoutes.getCustDetails, onSuccess, onError);


//     }



//     const doEditCustomer = () => {

//         const onSuccess = (res) => {
//             console.log('res', res)
//             dispatch(setLoader(false))
//             message.success("Customer updated successfully!");
//             history("/users");
//         }
//         const onError = (err) => {
//             console.log('err', err)
//             dispatch(setLoader(false))
//             alert(err?.data?.error);
//         }

//         if (!email) return message.error('Please enter a valid email');
//         if (!name) return message.error('Please enter name');
//         if (!phone) return message.error('Please enter phone');
//         if (!alternatePhone) return message.error('Please enter alternate number');
//         if (!licenseNumber) return message.error('Please enter license number');
//         if (!licenseExpDate) return message.error('Please enter license expiry date');
//         if (!postalCode) return message.error('Please enter postal code');
//         if (!address) return message.error('Please enter address');

        

//         const body = {
//             token: user?.token,
//             name: name,
//             email: email,
//             phone: phone,
//             alternate_phone: alternatePhone,
//             license_number: licenseNumber,
//             license_exp_date: licenseExpDate,
//             postal_code: postalCode,
//             address: address,
//             cust_id : id
//         };
//         if (email == data.email) delete body.email;
//         if (phone == data.phone) delete body.phone;

//         dispatch(setLoader(true));
//         postRequestFormData(body, apiRoutes.editCustomer, onSuccess, onError);
//     }


//     useEffect(() => {
//         getCustomerDetails();
//     }, [])


//     return (
//         <>
//             <ThemeProvider theme={theme}>
//                 <div style={{ display: 'flex', }}>
//                     <Sidebars />

//                     <div style={{ display: 'flex', flexDirection: 'column', marginLeft: 20, width: "75%" }} >
//                         <span className={`headertext ${isSmallScreen ? 'small-screen' : ''}`}>
//                             Edit Customer
//                         </span>
//                         <div style={{ display: 'flex', flexDirection: 'column', width: "100%", borderRadius: 10, backgroundColor: appColors.white, marginTop: 10, paddingBottom: 20 }}>
//                             <div style={{ width: "100%", height: 45, borderTopRightRadius: 10, borderTopLeftRadius: 10, backgroundColor: appColors.primary }}>
//                                 <Typography style={{ color: appColors.white, fontSize: 16, fontWeight: 600, marginLeft: 20, marginTop: 10 }}>Customer Information</Typography>
//                             </div>

//                             <Form
//                                 style={{ width: "100%", paddingRight: 20, paddingLeft: 20 }}
//                                 initialValues={{
//                                     // vehicleName: vehicle.veh_name,
//                                     // reg_no: vehicle.reg_no,
//                                     // make: vehicle.make,
//                                     // model: vehicle.model,
//                                     // year: vehicle.year,
//                                     // transmission: vehicle.transmission,
//                                     // mileage: vehicle.milage,
//                                     // airBags: vehicle.air_bags == "1" ? "Yes" : "No",
//                                     // fuelType: vehicle.fuel_type,
//                                     // seats: vehicle.seats,
//                                     // rent: vehicle.rent,
//                                     // bodyType: vehicle.body_type,
//                                     // rentalDetails: vehicle.rental_details,
//                                     // images: vehicle.images,
//                                 }}
//                                 layout="vertical"
//                                 form={form}
//                                 onFinish={() => doEditCustomer()}
//                                 onFinishFailed={() => { }}
//                             >
//                                 {/* Vehicle Name */}
//                                 <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', width: "100%", justifyContent: 'space-between', marginTop: 20 }} >
//                                     <Form.Item

//                                         name="name"
//                                     >
//                                         <Typography style={labelStyle}>Name</Typography>
//                                         <Input
//                                             className="textInput"
//                                             value={name}
//                                             onChange={(e) => setName(e.target.value)}
//                                         />
//                                     </Form.Item>

//                                     {/* Make */}
//                                     <Form.Item
//                                         name="email"
//                                     >
//                                         <Typography style={labelStyle}>Email</Typography>
//                                         <Input
//                                             className="textInput"
//                                             value={email}
//                                             onChange={(e) => setEmail(e.target.value)} />
//                                     </Form.Item>
//                                     <Form.Item
//                                         name="phone"
//                                     >
//                                         <Typography style={labelStyle}>Phone</Typography>
//                                         <Input
//                                             className="textInput"
//                                             value={phone} onChange={(e) => setPhone(e.target.value)} />
//                                     </Form.Item>

//                                     <Form.Item
//                                         name="alternatePhone"
//                                     >
//                                         <Typography style={labelStyle}>Alternate phone</Typography>
//                                         <Input
//                                             className="textInput"
//                                             value={alternatePhone} onChange={(e) => setAlternatePhone(e.target.value)} />
//                                     </Form.Item>



//                                 </div>

//                                 {/* Year */}
//                                 <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between', }} >

//                                     <Form.Item
//                                         name="licenseNumber"
//                                     >
//                                         <Typography style={labelStyle}>License Number</Typography>
//                                         <Input
//                                             className="textInput"
//                                             value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} />
//                                     </Form.Item>

//                                     <Form.Item
//                                         name="licenseExpiryDate"
//                                     >
//                                         <Typography style={labelStyle}>License expiry date</Typography>
//                                         <Input
//                                             className="textInput"
//                                             type="date"
//                                             // value={new Date(licenseExpDate).toLocaleString()}
//                                             value={licenseExpDate}
//                                             onChange={(e) => {
//                                                 console.log(e.target.value);
//                                                 setLicenseExpDate(e.target.value)
//                                             }} />
//                                     </Form.Item>

//                                     <Form.Item
//                                         name="postalCode"
//                                     >
//                                         <Typography style={labelStyle}>Postal code</Typography>
//                                         <Input
//                                             className="textInput"
//                                             typeof="date"
//                                             value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
//                                     </Form.Item>
//                                     <Form.Item
//                                         name="address"
//                                     >
//                                         <Typography style={labelStyle}>Address</Typography>
//                                         <Input
//                                             className="textInput"
//                                             typeof="date"
//                                             value={address} onChange={(e) => setAddress(e.target.value)} />
//                                     </Form.Item>

//                                 </div>




//                                 <Form.Item
//                                     style={{ textAlign: 'center', }}

//                                 >
//                                     <Button
//                                         style={{
//                                             height: 40, backgroundColor: appColors.primary, marginTop: 20,
//                                             fontFamily: 'Poppins', fontSize: 14, fontWeight: 600, width: 200
//                                         }}
//                                         type="primary"
//                                         htmlType="submit">

//                                         Save
//                                     </Button>
//                                 </Form.Item>



//                             </Form>


//                         </div>

//                     </div>
//                 </div>
//             </ThemeProvider>
//         </>
//     )

// }

// // const selectStyle = { width: 250 }

// const labelStyle = { color: appColors.black, fontSize: 14, fontFamily: 'Poppins', marginTop: 0 }

// export default EditUser
